/** @jsx h */
import { Fragment, h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import YourListDetail from './YourListDetail';
import { CheckIcon, CloseIcon, PencilIcon } from './yourListsIcons';
import { updateCart, removeCartLines } from '../objects/your-lists/updateCart';
import { loadEdits, applyEditsToItems } from '../objects/your-lists/listEdits';
import fetchListProducts, {
  isOutOfStock,
} from '../objects/your-lists/fetchListProducts';
import {
  getOrderLists,
  putOrderList,
  indexFromMetafieldKey,
  metafieldKeyFromIndex,
  findFreeSlotIndex,
  sortListsByLastUpdated,
} from '../objects/your-lists/orderListApi';
import CartResultModal from './CartResultModal';
import CartExtrasModal from './CartExtrasModal';

// Keep in sync with the modal transition duration in _your-lists.scss.
const MODAL_TRANSITION_MS = 250;

/**
 * Generate a reasonably unique id for a new list.
 * Falls back to a timestamp-based id where crypto.randomUUID is unavailable.
 */
function generateListId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `list-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

/** Format an ISO date as "28 March 2025". Returns '' for missing/invalid. */
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function YourLists({
  pageTitle = '',
  accessToken = '',
  badgeIcon = '',
  noImage = '',
  tagBadges = [],
  initialLists,
  freshLists = null,
  i18n = {},
}) {
  // `initialLists` (Liquid) lags behind Admin API writes — a list or item saved
  // moments ago from a PLP/PDP can be missing from it, and every edit here
  // writes a whole list back (COB2B-785). So the page waits for the server copy
  // (`freshLists`, requested by the entry as early as possible) instead of
  // rendering Liquid first and visibly swapping when it lands. Liquid is only
  // the fallback if that request fails, or what demo mode passes without one.
  const [lists, setLists] = useState(
    !freshLists && Array.isArray(initialLists) ? initialLists : [],
  );
  const [listsReady, setListsReady] = useState(!freshLists);
  useEffect(() => {
    if (!freshLists) return undefined;
    let cancelled = false;
    freshLists
      .then((fresh) => {
        if (!cancelled) setLists(fresh);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[your-lists] failed to load lists', err);
        if (!cancelled && Array.isArray(initialLists)) setLists(initialLists);
      })
      .finally(() => {
        if (!cancelled) setListsReady(true);
      });
    return () => {
      cancelled = true;
    };
    // Mount-only: both props are fixed for the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // null = dashboard view; otherwise the id of the list being viewed (detail).
  // Seeded from the `?list=<id>` URL param so a refresh stays on the detail.
  const [selectedListId, setSelectedListId] = useState(
    () => new URLSearchParams(window.location.search).get('list') || null,
  );

  // Navigate between dashboard/detail and keep it in the URL (?list=<id>).
  const openList = (id) => {
    setSelectedListId(id);
    const params = new URLSearchParams(window.location.search);
    if (id) params.set('list', id);
    else params.delete('list');
    const qs = params.toString();
    window.history.pushState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  };

  // Keep in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => {
      setSelectedListId(
        new URLSearchParams(window.location.search).get('list') || null,
      );
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Sync the page breadcrumb (rendered in Liquid, outside the app) with SPA
  // navigation: on the dashboard "Your Lists" is current; on a list its title
  // shows as the current crumb. The title crumb is added/removed from the DOM
  // (not just hidden) so "Your Lists" is genuinely the last child on the
  // dashboard and the CSS `:not(:last-child)` separator doesn't linger.
  useEffect(() => {
    const ol = document.querySelector('[data-yl-crumbs]');
    const listsCrumb = document.querySelector('[data-yl-crumb-lists]');
    if (!ol || !listsCrumb) return;

    let detailItem = ol.querySelector('[data-yl-crumb-detail]');
    const openListItem = lists.find((l) => l.id === selectedListId);

    if (openListItem) {
      listsCrumb.removeAttribute('aria-current');
      if (!detailItem) {
        detailItem = document.createElement('li');
        detailItem.className = 'breadcrumbs__item';
        detailItem.setAttribute('data-yl-crumb-detail', '');
        const link = document.createElement('a');
        link.className = 'breadcrumbs__link';
        link.setAttribute('aria-current', 'page');
        link.setAttribute('data-yl-crumb-title', '');
        detailItem.appendChild(link);
        ol.appendChild(detailItem);
      }
      detailItem.querySelector('[data-yl-crumb-title]').textContent =
        openListItem.name || '';
    } else {
      listsCrumb.setAttribute('aria-current', 'page');
      if (detailItem) detailItem.remove();
    }
  }, [selectedListId, lists]);

  // Intercept the "Your Lists" breadcrumb while viewing a list: navigate back
  // to the dashboard in-app (like "Back to Lists") instead of reloading.
  useEffect(() => {
    const listsCrumb = document.querySelector('[data-yl-crumb-lists]');
    if (!listsCrumb) return undefined;
    const onClick = (e) => {
      if (selectedListId) {
        e.preventDefault();
        openList(null);
      }
    };
    listsCrumb.addEventListener('click', onClick);
    return () => listsCrumb.removeEventListener('click', onClick);
  }, [selectedListId]);

  // Inline title editing on the dashboard: id of the row being renamed + draft.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const editInputRef = useRef(null);
  // Persists (independent of the transient message below) until the draft
  // changes again — a red underline on the input while it's wrong.
  const [renameError, setRenameError] = useState(false);
  // "Save failed" text that shows briefly then clears on its own.
  const [renameMessage, setRenameMessage] = useState('');
  const renameMessageTimer = useRef(null);

  // Focus the rename input when a row enters edit mode.
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Clear the transient message's timer on unmount.
  useEffect(() => () => clearTimeout(renameMessageTimer.current), []);

  const startEdit = (list) => {
    setEditingId(list.id);
    setEditDraft(list.name);
    setRenameError(false);
    setRenameMessage('');
    clearTimeout(renameMessageTimer.current);
  };

  const confirmEdit = async (list) => {
    const next = editDraft.trim();
    if (!next || next === list.name) {
      setEditingId(null);
      return;
    }

    const saved = await renameList(list.id, next);
    if (saved) {
      setRenameError(false);
      setEditingId(null);
      return;
    }

    // Stay in edit mode so the user can see the error and retry.
    setRenameError(true);
    setRenameMessage(t('save_failed'));
    clearTimeout(renameMessageTimer.current);
    renameMessageTimer.current = setTimeout(() => setRenameMessage(''), 2000);
    if (editInputRef.current) editInputRef.current.focus();
  };

  const handleEditKey = (e, list) => {
    if (e.key === 'Enter') confirmEdit(list);
    else if (e.key === 'Escape') {
      setEditingId(null);
      setRenameError(false);
      setRenameMessage('');
      clearTimeout(renameMessageTimer.current);
    }
  };
  const [isModalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Transition state: `isRendered` keeps the modal mounted during the exit
  // animation; `isActive` toggles the CSS class that drives enter/leave.
  const [isRendered, setRendered] = useState(false);
  const [isActive, setActive] = useState(false);

  // Translate helper — falls back to the key if a string is missing.
  const t = (key) => i18n[key] || key;

  const openModal = () => {
    setName('');
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return; // don't allow closing mid-save
    setModalOpen(false);
  };

  // Drive enter/leave transitions off `isModalOpen`.
  // MODAL_TRANSITION_MS must match the CSS transition duration.
  useEffect(() => {
    if (isModalOpen) {
      setRendered(true);
      // Wait a frame so the initial (closed) styles paint before flipping to
      // the active state, otherwise the browser skips the enter transition.
      const raf = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(raf);
    }

    setActive(false);
    const timer = setTimeout(() => setRendered(false), MODAL_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isModalOpen]);

  // While the modal is open: close on Esc and lock background scrolling.
  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        setModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isModalOpen, saving]);

  const handleCreate = async (event) => {
    event.preventDefault();

    const trimmed = name.trim();
    if (!trimmed || saving) return;

    setSaving(true);
    setError(null);

    try {
      // There's no "create" endpoint — each list is its own company metafield
      // slot (order_list_1..30), so creating a list means claiming the lowest
      // free slot and writing straight into it. The slot comes from the server
      // copy: one that only looks free here would overwrite a list (COB2B-785).
      const fresh = await getOrderLists();
      const freeIndex = findFreeSlotIndex(fresh);
      if (freeIndex === null) {
        setError(t('list_limit_reached'));
        return;
      }

      const newList = {
        id: generateListId(),
        name: trimmed,
        items: [],
        createdAt: new Date().toISOString(),
        metafieldKey: metafieldKeyFromIndex(freeIndex),
      };

      await putOrderList(freeIndex, toStoredValue(newList));
      // Only commit to local state once the server has confirmed the write.
      setLists([...fresh, newList]);
      setModalOpen(false);
    } catch (err) {
      setError(t('create_error'));
    } finally {
      setSaving(false);
    }
  };

  const hasLists = lists.length > 0;
  // Dashboard shows the most recently updated list first.
  const sortedLists = sortListsByLastUpdated(lists);
  const selectedList = lists.find((l) => l.id === selectedListId) || null;

  // The value we'd write back to a slot metafield: the list without the
  // client-only fields attached on read (normalizeLists in preact-your-lists.js).
  const toStoredValue = (list) => {
    if (!list) return null;
    const { metafieldKey, ...value } = list;
    return value;
  };

  // Apply a patch to a list and stamp `updatedAt`. Every edit that changes a
  // list (rename, or an item's quantity/selected/removed) goes through here so
  // the "Last updated" date reflects it. Updates optimistically, then persists
  // to the list's own company metafield slot; on failure the optimistic update
  // is rolled back.
  const touchList = async (id, patch = {}) => {
    const updatedAt = new Date().toISOString();
    const current = lists.find((l) => l.id === id);
    const updatedList = { ...current, ...patch, updatedAt };
    setLists((prev) =>
      prev.map((l) => (l.id === id ? updatedList : l)),
    );

    const index = indexFromMetafieldKey(current.metafieldKey);
    if (!index) {
      setLists((prev) => prev.map((l) => (l.id === id ? current : l)));
      // eslint-disable-next-line no-console
      console.error(`touchList: missing/invalid metafieldKey on list ${id}`);
      return false;
    }

    try {
      await putOrderList(index, toStoredValue(updatedList));
      return true;
    } catch (err) {
      // The write didn't land — revert so the UI doesn't lie about saved state.
      setLists((prev) => prev.map((l) => (l.id === id ? current : l)));
      // eslint-disable-next-line no-console
      console.error('[your-lists] failed to save list', { id, err });
      return false;
    }
  };

  // Rename a list (from either the dashboard row or the detail title editor).
  // Callers only invoke this when the name actually changed.
  const renameList = (id, name) => touchList(id, { name });

  // Add a list's selected products to the cart (same logic as the detail view).
  const [cartBusyId, setCartBusyId] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Cart update result report ({ lines }) shown in a modal, or null.
  const [cartResult, setCartResult] = useState(null);
  // Cart products not in the list just added ([{ id, title, quantity, image }]),
  // or null: shown in a modal asking the user to keep or remove them.
  const [cartExtras, setCartExtras] = useState(null);

  const addListToCart = async (list) => {
    // Use the user's unsaved detail-view edits (quantity / selected / removed)
    // if any are stored locally, so adding from the dashboard reflects them.
    const items = applyEditsToItems(list.items, loadEdits(list.id));
    const selected = items.filter((it) => it.selected);
    if (selected.length === 0 || cartBusyId) return;

    setCartBusyId(list.id);
    try {
      // Resolve stock: in-stock items go to the cart; out-of-stock (or
      // unresolved) selected items are reported as skipped.
      const products = await fetchListProducts(
        selected.map((it) => it.id),
        accessToken,
      );
      const inStock = [];
      const skipped = [];
      selected.forEach((it) => {
        const p = products[it.id];
        if (p && !isOutOfStock(p)) {
          inStock.push({
            id: it.id,
            quantity: it.quantity ?? 0,
            title: it.title || p.title,
          });
        } else {
          skipped.push({ id: it.id, title: it.title || p?.title || String(it.id), oos: true });
        }
      });

      // Full list membership so cart products belonging to the list aren't
      // flagged as extras (passing listItemIds also computes extras even when
      // nothing new is added).
      const listItemIds = items.map((it) => it.id);
      const report = await updateCart(inStock, listItemIds);
      // Show the keep/remove prompt when the cart has products outside the list.
      if (report.extras && report.extras.length) {
        setCartExtras(report.extras);
      }
      // NOTE: the "added / updated" result report is disabled for now — keep the
      // code so it can be re-enabled later.
      // const lines = [...report.lines, ...skipped];
      // else if (lines.length) {
      //   setCartResult({ lines });
      // }
      setAddedIds((prev) => new Set(prev).add(list.id));
    } catch (e) {
      // TODO: surface a cart-update error to the user
    } finally {
      setCartBusyId(null);
    }
  };

  // "Remove" on the extras prompt: drop the non-list products from the cart.
  const handleRemoveExtras = async () => {
    if (!cartExtras || cartExtras.length === 0) return;
    await removeCartLines(cartExtras.map((e) => e.id));
    setCartExtras(null);
  };

  // Delete a list — confirmation modal (id pending deletion, null = closed).
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openDeleteConfirm = (id) => {
    setDeleteError(null);
    setConfirmDeleteId(id);
  };

  // Removes the list's own metafield slot by PUTting a `null` value — the
  // backend has no dedicated DELETE, and a null value round-trips through
  // GET as the same "unset" state as a slot that was never written.
  const deleteList = async (id) => {
    const current = lists.find((l) => l.id === id);
    if (!current) return true;

    const index = indexFromMetafieldKey(current.metafieldKey);
    setLists((prev) => prev.filter((l) => l.id !== id));

    if (!index) {
      setLists((prev) => [...prev, current]);
      // eslint-disable-next-line no-console
      console.error(`deleteList: missing/invalid metafieldKey on list ${id}`);
      return false;
    }

    try {
      await putOrderList(index, null);
      return true;
    } catch (err) {
      // The write didn't land — put the list back.
      setLists((prev) => [...prev, current]);
      // eslint-disable-next-line no-console
      console.error('[your-lists] failed to delete list', { id, err });
      return false;
    }
  };

  const confirmDeleteList = async () => {
    setDeleting(true);
    setDeleteError(null);
    const ok = await deleteList(confirmDeleteId);
    setDeleting(false);
    if (ok) {
      setConfirmDeleteId(null);
    } else {
      setDeleteError(t('delete_error'));
    }
  };

  // Close the delete confirmation on Esc + lock background scroll while open.
  // Blocked while a delete is in flight, same as the create modal.
  useEffect(() => {
    if (confirmDeleteId == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !deleting) setConfirmDeleteId(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [confirmDeleteId, deleting]);

  if (!listsReady) {
    return (
      <Fragment>
        {!selectedListId && (
          <div className="page--yl-header">
            <h1 className="page--title">{pageTitle}</h1>
          </div>
        )}
        <div className="page--ld-state">{t('loading')}</div>
      </Fragment>
    );
  }

  // Detail view (after "View List"). "Back to Lists" returns here.
  if (selectedList) {
    return (
      <YourListDetail
        list={selectedList}
        i18n={i18n}
        accessToken={accessToken}
        badgeIcon={badgeIcon}
        noImage={noImage}
        tagBadges={tagBadges}
        onBack={() => openList(null)}
        onRename={(name) => renameList(selectedList.id, name)}
        onListChange={(items) =>
          touchList(selectedList.id, items ? { items } : {})
        }
      />
    );
  }

  return (
    <Fragment>
      <div className="page--yl-header">
        <h1 className="page--title">{pageTitle}</h1>
        {hasLists && (
          <button
            type="button"
            className="page--yl-create"
            onClick={openModal}
          >
            {t('create_list')}
          </button>
        )}
      </div>

      {hasLists ? (
        <ul className="page--lists">
          {sortedLists.map((list) => (
            <li className="page--list-row" key={list.id}>
              <div className="page--list-row-main">
                <div className="page--list-row-info">
                  {editingId === list.id ? (
                    <Fragment>
                      <input
                        ref={editInputRef}
                        type="text"
                        className={`page--list-row-name-input${renameError ? ' is-error' : ''}`}
                        value={editDraft}
                        aria-label={t('edit_list')}
                        onInput={(e) => {
                          setEditDraft(e.currentTarget.value);
                          setRenameError(false);
                        }}
                        onKeyDown={(e) => handleEditKey(e, list)}
                        onBlur={() => confirmEdit(list)}
                      />
                      {renameMessage && (
                        <span className="page--list-row-name-message" role="status">
                          {renameMessage}
                        </span>
                      )}
                    </Fragment>
                  ) : (
                    <span className="page--list-row-name">{list.name}</span>
                  )}
                  <span className="page--list-row-updated">
                    {t('last_updated')} {formatDate(list.updatedAt || list.createdAt)}
                  </span>
                </div>
                {editingId === list.id ? (
                  <button
                    type="button"
                    className="page--list-row-edit"
                    aria-label={t('save')}
                    onClick={() => confirmEdit(list)}
                  >
                    <CheckIcon />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="page--list-row-edit"
                    aria-label={t('edit_list')}
                    onClick={() => startEdit(list)}
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>

              {/* TODO(actions): View List / Add to Cart / remove are render-only */}
              <div className="page--list-row-actions">
                <button
                  type="button"
                  className="page--list-action page--list-action--view"
                  onClick={() => openList(list.id)}
                >
                  {t('view_list')}
                </button>
                <button
                  type="button"
                  className={`page--list-action page--list-action--cart${
                    addedIds.has(list.id) ? ' is-added' : ''
                  }${cartBusyId === list.id ? ' is-loading' : ''}`}
                  disabled={
                    cartBusyId === list.id ||
                    addedIds.has(list.id) ||
                    !applyEditsToItems(list.items, loadEdits(list.id)).some(
                      (it) => it.selected,
                    )
                  }
                  onClick={() => addListToCart(list)}
                >
                  {/* Label stays in the layout (hidden while adding) so the
                      button keeps its width; the dots overlay it. */}
                  <span className="page--list-action-label">
                    {addedIds.has(list.id)
                      ? t('list_added_to_cart')
                      : t('add_list_to_cart')}
                  </span>
                  {cartBusyId === list.id && (
                    <span
                      className="page--ld-dots"
                      role="status"
                      aria-label={t('adding_list_to_cart')}
                    >
                      <span />
                      <span />
                      <span />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="page--list-action page--list-action--remove"
                  aria-label={t('remove_list')}
                  onClick={() => openDeleteConfirm(list.id)}
                >
                  <CloseIcon />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="page--no-lists">
          <div className="page--no-lists-title">{t('no_lists_title')}</div>
          <div className="page--no-lists-description">
            {t('no_lists_description')}
          </div>
          <button
            type="button"
            className="page--yl-create page--yl-create--no-lists"
            onClick={openModal}
          >
            {t('create_list')}
          </button>
        </div>
      )}

      {isRendered && (
        <div
          className={`page--yl-modal-overlay${isActive ? ' is-open' : ''}`}
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="page--yl-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="page--yl-modal-close"
              aria-label={t('cancel')}
              disabled={saving}
              onClick={closeModal}
            >
              <CloseIcon />
            </button>

            <form onSubmit={handleCreate}>
              <div className="page--yl-modal-title">{t('create_list_title')}</div>

              <label className="page--yl-modal-label" htmlFor="yl-new-list-name">
                {t('list_name_label')}
              </label>
              <input
                id="yl-new-list-name"
                className="page--yl-modal-input"
                type="text"
                value={name}
                placeholder={t('list_name_placeholder')}
                disabled={saving}
                autoFocus
                onInput={(e) => setName(e.target.value)}
              />

              {error && <div className="page--yl-modal-error">{error}</div>}

              <div className="page--yl-modal-actions">
                <button
                  type="submit"
                  className="button button--primary page--yl-modal-button"
                  disabled={saving || !name.trim()}
                >
                  {saving ? t('creating') : t('save')}
                </button>
                <button
                  type="button"
                  className="button button--secondary page--yl-modal-button"
                  disabled={saving}
                  onClick={closeModal}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDeleteId != null && (
        <div
          className="page--ld-confirm-overlay"
          role="presentation"
          onClick={() => !deleting && setConfirmDeleteId(null)}
        >
          <div
            className="page--ld-confirm"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="page--ld-confirm-close"
              aria-label={t('cancel')}
              onClick={() => setConfirmDeleteId(null)}
              disabled={deleting}
            >
              <CloseIcon />
            </button>

            <div className="page--ld-confirm-title">
              {t('delete_list_title')}
            </div>
            <div className="page--ld-confirm-subtitle">
              {t('delete_list_subtitle')}
            </div>
            {deleteError && (
              <div className="page--ld-confirm-error">{deleteError}</div>
            )}

            <div className="page--ld-confirm-actions">
              <button
                type="button"
                className="page--ld-confirm-delete"
                onClick={confirmDeleteList}
                disabled={deleting}
              >
                {deleting ? t('deleting') : t('delete')}
              </button>
              <button
                type="button"
                className="page--ld-confirm-cancel"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deleting}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Added / updated result report — disabled for now, kept for later.
      <CartResultModal
        report={cartResult}
        i18n={i18n}
        onClose={() => setCartResult(null)}
      />
      */}

      <CartExtrasModal
        extras={cartExtras}
        i18n={i18n}
        onKeep={() => setCartExtras(null)}
        onRemove={handleRemoveExtras}
      />
    </Fragment>
  );
}

export default YourLists;
